import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, Inbox, Star, Check, Trash2, Plus, Pencil } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { formatPrice } from "../data/products";
import ProductForm from "../components/ProductForm";
import AdminHomepage from "../components/AdminHomepage";
import AdminContact from "../components/AdminContact";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { t, lang, productMedia, refreshProducts, options, refreshOptions, seriesLabel } = useShop();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const loadData = () => {
    Promise.all([
      axios.get(`${API}/admin/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/reviews`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/products`).catch(() => ({ data: [] })),
    ])
      .then(([q, r, p]) => {
        setQuotes(q.data || []);
        setReviews(r.data || []);
        setProducts(p.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const approveReview = async (id) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}/approve`, {}, { withCredentials: true });
      toast.success(t.reviews.approvedToast);
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, status: "approved" } : r)));
    } catch {
      toast.error(t.reviews.error);
    }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
      toast.success(t.reviews.deletedToast);
      setReviews((rs) => rs.filter((r) => r.id !== id));
    } catch {
      toast.error(t.reviews.error);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true });
      toast.success(t.adminProducts.deleted);
      setProducts((ps) => ps.filter((p) => p.id !== id));
      refreshProducts();
    } catch {
      toast.error(t.adminProducts.error);
    }
  };

  const handleSaved = () => {
    loadData();
    refreshProducts();
  };

  const s = t.adminSettings;
  const [newSeries, setNewSeries] = useState({ label_tr: "", label_en: "" });
  const [newDims, setNewDims] = useState("");
  const [editingSeries, setEditingSeries] = useState(null);
  const [editLabels, setEditLabels] = useState({ label_tr: "", label_en: "" });

  const apiError = (err) => {
    const detail = err.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : s.error);
  };

  const addSeries = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/options/series`, newSeries, { withCredentials: true });
      toast.success(s.added);
      setNewSeries({ label_tr: "", label_en: "" });
      refreshOptions();
    } catch (err) {
      apiError(err);
    }
  };

  const renameSeries = async (key) => {
    try {
      await axios.patch(`${API}/admin/options/series/${key}`, editLabels, { withCredentials: true });
      toast.success(s.updated);
      setEditingSeries(null);
      refreshOptions();
    } catch (err) {
      apiError(err);
    }
  };

  const deleteSeries = async (key) => {
    try {
      await axios.delete(`${API}/admin/options/series/${key}`, { withCredentials: true });
      toast.success(s.deleted);
      refreshOptions();
    } catch (err) {
      apiError(err);
    }
  };

  const addDims = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/options/dims`, { value: newDims }, { withCredentials: true });
      toast.success(s.added);
      setNewDims("");
      refreshOptions();
    } catch (err) {
      apiError(err);
    }
  };

  const deleteDims = async (value) => {
    try {
      await axios.delete(`${API}/admin/options/dims/${encodeURIComponent(value)}`, { withCredentials: true });
      toast.success(s.deleted);
      refreshOptions();
    } catch (err) {
      apiError(err);
    }
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const pendingCount = reviews.filter((r) => r.status === "pending").length;
  const sortedReviews = [...reviews].sort((a, b) =>
    a.status === b.status ? 0 : a.status === "pending" ? -1 : 1
  );

  const tabCls = (name) =>
    `pb-4 pr-8 font-serif text-xl sm:text-2xl font-medium transition-colors duration-300 border-b-2 -mb-px flex items-center gap-3 ${
      tab === name
        ? "text-brown border-olive"
        : "text-brown-muted border-transparent hover:text-brown-light"
    }`;

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="admin-page">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-brown/10">
        <Link to="/" className="flex flex-col leading-none" data-testid="admin-logo">
          <span className="font-serif text-2xl font-medium tracking-tight text-brown">VLP Dolap</span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-brown-muted mt-1">Volpar</span>
        </Link>
        <div className="flex items-center gap-5">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-brown-light hover:text-brown transition-colors duration-300"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={15} /> {t.auth.backHome}
          </Link>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-brown-light hover:text-brown transition-colors duration-300"
            data-testid="admin-logout-button"
          >
            <LogOut size={15} /> {t.auth.logout}
          </button>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-12 py-16 max-w-[1200px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-dark mb-4">
            {t.auth.adminOverline}
          </p>

          <div className="flex flex-wrap border-b border-brown/10 mb-8" data-testid="admin-tabs">
            <button onClick={() => setTab("products")} className={tabCls("products")} data-testid="admin-tab-products">
              {t.adminProducts.tab}
            </button>
            <button onClick={() => setTab("quotes")} className={tabCls("quotes")} data-testid="admin-tab-quotes">
              {t.reviews.quotesTab}
            </button>
            <button onClick={() => setTab("reviews")} className={tabCls("reviews")} data-testid="admin-tab-reviews">
              {t.reviews.reviewsTab}
              {pendingCount > 0 && (
                <span
                  className="min-w-6 h-6 px-1.5 rounded-full bg-olive text-cream text-xs font-sans font-semibold flex items-center justify-center"
                  data-testid="pending-reviews-badge"
                >
                  {pendingCount}
                </span>
              )}
            </button>
            <button onClick={() => setTab("settings")} className={tabCls("settings")} data-testid="admin-tab-settings">
              {s.tab}
            </button>
            <button onClick={() => setTab("homepage")} className={tabCls("homepage")} data-testid="admin-tab-homepage">
              {t.adminHome.tab}
            </button>
            <button onClick={() => setTab("contact")} className={tabCls("contact")} data-testid="admin-tab-contact">
              {t.adminContact.tab}
            </button>
          </div>

          {tab === "contact" && <AdminContact />}

          {tab === "homepage" && <AdminHomepage />}

          {tab === "products" && (
            <>
              <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                  <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown">{t.adminProducts.tab}</h1>
                  <p className="mt-4 text-base font-light text-brown-light max-w-xl">{t.adminProducts.sub}</p>
                </div>
                <button
                  onClick={() => {
                    setEditingProduct(null);
                    setFormOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark transition-colors duration-300"
                  data-testid="admin-add-product"
                >
                  <Plus size={16} /> {t.adminProducts.add}
                </button>
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-products-list">
                {loading ? (
                  <p className="text-brown-muted font-light">{t.auth.loading}</p>
                ) : products.length === 0 ? (
                  <div className="md:col-span-2 border border-brown/10 bg-cream-dark p-12 flex flex-col items-center gap-4 text-center">
                    <Inbox size={32} className="text-brown-muted" strokeWidth={1.25} />
                    <p className="text-brown-light font-light" data-testid="admin-no-products">{t.adminProducts.noProducts}</p>
                  </div>
                ) : (
                  products.map((p) => (
                    <div
                      key={p.id}
                      className="border border-brown/10 bg-cream-dark p-6 flex gap-5"
                      data-testid={`admin-product-card-${p.id}`}
                    >
                      <div className="w-20 h-24 shrink-0 overflow-hidden bg-cream border border-brown/10">
                        {productMedia(p) && (
                          <img src={productMedia(p)} alt={p.name?.tr} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-serif text-lg font-medium text-brown">{p.name?.[lang] || p.name?.tr}</p>
                          {p.bestseller && (
                            <span className="px-2.5 py-0.5 rounded-full bg-olive text-cream text-[10px] tracking-[0.15em] uppercase font-semibold">
                              {t.products.bestseller}
                            </span>
                          )}
                          {p.video_path && (
                            <span className="px-2.5 py-0.5 rounded-full bg-brown/10 text-brown-light text-[10px] tracking-[0.15em] uppercase font-semibold">
                              {t.adminProducts.hasVideo}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-brown-muted">
                          {p.dims} · {seriesLabel(p.series)}
                        </p>
                        <p className="mt-2 text-sm font-medium text-brown">{formatPrice(p.price)}</p>
                        <div className="mt-4 flex gap-3">
                          <button
                            onClick={() => {
                              setEditingProduct(p);
                              setFormOpen(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brown text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors duration-300"
                            data-testid={`admin-edit-${p.id}`}
                          >
                            <Pencil size={12} /> {t.adminProducts.edit}
                          </button>
                          <button
                            onClick={() => deleteProduct(p.id)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-800/30 text-red-800 text-xs font-medium tracking-widest uppercase hover:bg-red-800/5 transition-colors duration-300"
                            data-testid={`admin-delete-product-${p.id}`}
                          >
                            <Trash2 size={12} /> {t.adminProducts.delete}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {tab === "quotes" && (
            <>
              <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown">{t.auth.adminPanel}</h1>
              <p className="mt-4 text-base font-light text-brown-light max-w-xl">{t.auth.adminPanelSub}</p>
              <div className="mt-12" data-testid="admin-quotes-list">
                {loading ? (
                  <p className="text-brown-muted font-light">{t.auth.loading}</p>
                ) : quotes.length === 0 ? (
                  <div className="border border-brown/10 bg-cream-dark p-12 flex flex-col items-center gap-4 text-center">
                    <Inbox size={32} className="text-brown-muted" strokeWidth={1.25} />
                    <p className="text-brown-light font-light" data-testid="admin-no-quotes">{t.auth.noQuotes}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-brown/10 bg-cream-dark">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-brown/10 text-left">
                          {[t.auth.cols.date, t.auth.cols.name, t.auth.cols.company, t.auth.cols.phone, t.auth.cols.quantity, t.auth.cols.note].map(
                            (col) => (
                              <th key={col} className="px-5 py-4 text-[11px] tracking-[0.2em] uppercase text-brown-muted font-semibold whitespace-nowrap">
                                {col}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((q) => (
                          <tr key={q.id} className="border-b border-brown/5 last:border-0 hover:bg-olive/5 transition-colors" data-testid={`admin-quote-row-${q.id}`}>
                            <td className="px-5 py-4 whitespace-nowrap text-brown-light">{fmtDate(q.created_at)}</td>
                            <td className="px-5 py-4 font-medium text-brown whitespace-nowrap">{q.name}</td>
                            <td className="px-5 py-4 text-brown-light">{q.company}</td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="text-olive-dark hover:underline">
                                {q.phone}
                              </a>
                            </td>
                            <td className="px-5 py-4 text-brown font-medium">{q.quantity}</td>
                            <td className="px-5 py-4 text-brown-light max-w-[220px] truncate">{q.note || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "reviews" && (
            <>
              <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown">{t.reviews.reviewsTab}</h1>
              <p className="mt-4 text-base font-light text-brown-light max-w-xl">{t.reviews.adminReviewsSub}</p>
              <div className="mt-12 flex flex-col gap-4" data-testid="admin-reviews-list">
                {loading ? (
                  <p className="text-brown-muted font-light">{t.auth.loading}</p>
                ) : sortedReviews.length === 0 ? (
                  <div className="border border-brown/10 bg-cream-dark p-12 flex flex-col items-center gap-4 text-center">
                    <Inbox size={32} className="text-brown-muted" strokeWidth={1.25} />
                    <p className="text-brown-light font-light" data-testid="admin-no-reviews">{t.reviews.noReviews}</p>
                  </div>
                ) : (
                  sortedReviews.map((r) => (
                    <div
                      key={r.id}
                      className={`border p-6 md:p-8 flex flex-col md:flex-row md:items-start gap-6 ${
                        r.status === "pending" ? "border-olive/40 bg-olive/5" : "border-brown/10 bg-cream-dark"
                      }`}
                      data-testid={`admin-review-card-${r.id}`}
                    >
                      {r.photo_path && (
                        <img
                          src={`${API}/reviews/${r.id}/photo`}
                          alt=""
                          className="w-24 h-24 object-cover border border-brown/10 shrink-0"
                          data-testid={`admin-review-photo-${r.id}`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <p className="font-medium text-brown">{r.name}</p>
                          <span className="flex gap-0.5 text-olive-dark">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} size={13} fill={i < r.rating ? "currentColor" : "none"} strokeWidth={1.5} />
                            ))}
                          </span>
                          <span
                            className={`px-3 py-0.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold ${
                              r.status === "pending" ? "bg-olive text-cream" : "bg-brown/10 text-brown-light"
                            }`}
                            data-testid={`admin-review-status-${r.id}`}
                          >
                            {r.status === "pending" ? t.reviews.pending : t.reviews.approved}
                          </span>
                        </div>
                        <p className="mt-3 text-sm font-light text-brown-light leading-relaxed">{r.comment}</p>
                        <p className="mt-3 text-xs text-brown-muted">{fmtDate(r.created_at)}</p>
                      </div>
                      <div className="flex md:flex-col gap-3 shrink-0">
                        {r.status === "pending" && (
                          <button
                            onClick={() => approveReview(r.id)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-olive text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors duration-300"
                            data-testid={`admin-approve-${r.id}`}
                          >
                            <Check size={14} /> {t.reviews.approve}
                          </button>
                        )}
                        <button
                          onClick={() => deleteReview(r.id)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-800/30 text-red-800 text-xs font-medium tracking-widest uppercase hover:bg-red-800/5 transition-colors duration-300"
                          data-testid={`admin-delete-${r.id}`}
                        >
                          <Trash2 size={14} /> {t.reviews.delete}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
          {tab === "settings" && (
            <>
              <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown">{s.tab}</h1>
              <p className="mt-4 text-base font-light text-brown-light max-w-xl">{s.sub}</p>

              <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-settings">
                <div className="border border-brown/10 bg-cream-dark p-8">
                  <h2 className="font-serif text-2xl font-medium text-brown mb-6">{s.seriesTitle}</h2>
                  <div className="flex flex-col gap-3" data-testid="settings-series-list">
                    {(options?.series || []).length === 0 ? (
                      <p className="text-sm text-brown-muted font-light">{s.emptySeries}</p>
                    ) : (
                      (options?.series || []).map((o) => (
                        <div
                          key={o.key}
                          className="border border-brown/10 bg-cream p-4"
                          data-testid={`settings-series-${o.key}`}
                        >
                          {editingSeries === o.key ? (
                            <div className="flex flex-col gap-3">
                              <Input
                                value={editLabels.label_tr}
                                onChange={(e) => setEditLabels((l) => ({ ...l, label_tr: e.target.value }))}
                                placeholder={s.labelTr}
                                className="editorial-input"
                                data-testid={`settings-series-edit-tr-${o.key}`}
                              />
                              <Input
                                value={editLabels.label_en}
                                onChange={(e) => setEditLabels((l) => ({ ...l, label_en: e.target.value }))}
                                placeholder={s.labelEn}
                                className="editorial-input"
                                data-testid={`settings-series-edit-en-${o.key}`}
                              />
                              <div className="flex gap-3">
                                <button
                                  onClick={() => renameSeries(o.key)}
                                  className="px-4 py-2 rounded-full bg-olive text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors"
                                  data-testid={`settings-series-save-${o.key}`}
                                >
                                  {s.save}
                                </button>
                                <button
                                  onClick={() => setEditingSeries(null)}
                                  className="px-4 py-2 rounded-full border border-brown/20 text-brown-light text-xs font-medium tracking-widest uppercase hover:text-brown transition-colors"
                                  data-testid={`settings-series-cancel-${o.key}`}
                                >
                                  {s.cancel}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-brown">{o.label_tr} · {o.label_en}</p>
                                <p className="text-[11px] tracking-[0.2em] uppercase text-brown-muted mt-1">{o.key}</p>
                              </div>
                              <div className="flex gap-2 shrink-0">
                                <button
                                  onClick={() => {
                                    setEditingSeries(o.key);
                                    setEditLabels({ label_tr: o.label_tr, label_en: o.label_en });
                                  }}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brown text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors"
                                  data-testid={`settings-series-edit-${o.key}`}
                                >
                                  <Pencil size={12} /> {s.edit}
                                </button>
                                <button
                                  onClick={() => deleteSeries(o.key)}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-800/30 text-red-800 text-xs font-medium tracking-widest uppercase hover:bg-red-800/5 transition-colors"
                                  data-testid={`settings-series-delete-${o.key}`}
                                >
                                  <Trash2 size={12} /> {s.delete}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <form onSubmit={addSeries} className="mt-6 border-t border-brown/10 pt-6 flex flex-col gap-3" data-testid="settings-series-add-form">
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        required
                        minLength={2}
                        value={newSeries.label_tr}
                        onChange={(e) => setNewSeries((n) => ({ ...n, label_tr: e.target.value }))}
                        placeholder={s.labelTr}
                        className="editorial-input"
                        data-testid="settings-series-new-tr"
                      />
                      <Input
                        required
                        minLength={2}
                        value={newSeries.label_en}
                        onChange={(e) => setNewSeries((n) => ({ ...n, label_en: e.target.value }))}
                        placeholder={s.labelEn}
                        className="editorial-input"
                        data-testid="settings-series-new-en"
                      />
                    </div>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brown text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors"
                      data-testid="settings-series-add"
                    >
                      <Plus size={14} /> {s.addSeries}
                    </button>
                  </form>
                </div>

                <div className="border border-brown/10 bg-cream-dark p-8">
                  <h2 className="font-serif text-2xl font-medium text-brown mb-6">{s.dimsTitle}</h2>
                  <div className="flex flex-col gap-3" data-testid="settings-dims-list">
                    {(options?.dims || []).length === 0 ? (
                      <p className="text-sm text-brown-muted font-light">{s.emptyDims}</p>
                    ) : (
                      (options?.dims || []).map((value) => (
                        <div
                          key={value}
                          className="border border-brown/10 bg-cream p-4 flex items-center justify-between gap-4"
                          data-testid={`settings-dims-${value.replace(/[^0-9]/g, "-")}`}
                        >
                          <p className="text-sm font-medium text-brown">{value}</p>
                          <button
                            onClick={() => deleteDims(value)}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-800/30 text-red-800 text-xs font-medium tracking-widest uppercase hover:bg-red-800/5 transition-colors shrink-0"
                            data-testid={`settings-dims-delete-${value.replace(/[^0-9]/g, "-")}`}
                          >
                            <Trash2 size={12} /> {s.delete}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <form onSubmit={addDims} className="mt-6 border-t border-brown/10 pt-6 flex flex-col gap-3" data-testid="settings-dims-add-form">
                    <Input
                      required
                      minLength={3}
                      value={newDims}
                      onChange={(e) => setNewDims(e.target.value)}
                      placeholder={s.dimsPlaceholder}
                      className="editorial-input"
                      data-testid="settings-dims-new"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brown text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors"
                      data-testid="settings-dims-add"
                    >
                      <Plus size={14} /> {s.addDims}
                    </button>
                  </form>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </main>

      <ProductForm
        open={formOpen}
        onOpenChange={setFormOpen}
        product={editingProduct}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default AdminPage;
